import { api } from '../axios'
import type { ApiResponse } from '../../../types'

export interface JoiningReport {
  id: number
  teacher_id: number
  transfer_id?: number
  joining_date: string
  remarks?: string
  file_path: string
  file_name: string
  original_name: string
  file_size: number
  mime_type: string
  status: 'submitted' | 'completed'
  created_at: string
  updated_at?: string
  teachers?: {
    id: number
    teacher_name: string
    schools?: {
      school_name: string
      school_id: string
    }
  }
  transfer_applications?: {
    id: number
    application_number: string
    preferredSchoolId?: string
    status?: string
  }
  users?: {
    id: number
    username: string
    email: string
  }
  joining_report_recipients?: Array<{
    id: number
    recipient_role: string
    viewed: boolean
    viewed_at?: string
    verified: boolean
    verified_at?: string
    verification_notes?: string
  }>
  approverHierarchy?: {
    branch?: 'DIRECTORATE' | 'SECRETARIAT' | null
    skipHoi?: boolean
    skipSdeo?: boolean
    skipDeo?: boolean
    hoiId?: number | null
    sdeoId?: number | null
    deoId?: number | null
  } | null
}

export interface CreateJoiningReportData {
  teacher_id: number
  transfer_id?: number
  joining_date: string
  remarks?: string
  recipients: Array<{
    role: string
    office_id?: string
  }>
  file?: File
}

export interface JoiningReportFilters {
  status?: string
  teacher_id?: number
  teacher_name?: string
  school_id?: string
  page?: number
  limit?: number
  role?: string
}

export const joiningReportsApi = {
  upload: (data: CreateJoiningReportData) => {
    const formData = new FormData()
    if (data.file) {
      formData.append('file', data.file)
    }
    formData.append('teacher_id', data.teacher_id.toString())
    if (data.transfer_id) {
      formData.append('transfer_id', data.transfer_id.toString())
    }
    formData.append('joining_date', data.joining_date)
    if (data.remarks) {
      formData.append('remarks', data.remarks)
    }
    formData.append('recipients', JSON.stringify(data.recipients))

    return api.post<ApiResponse<JoiningReport>>('/joining-reports/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  getAll: (filters: JoiningReportFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.status) params.append('status', filters.status)
    if (filters.teacher_id) params.append('teacher_id', filters.teacher_id.toString())
    if (filters.school_id) params.append('school_id', filters.school_id)
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.role) params.append('role', filters.role)

    return api.get<ApiResponse<JoiningReport[]> & { pagination?: any }>(`/joining-reports?${params.toString()}`)
  },

  getTeachers: (schoolId?: string) => {
    const params = new URLSearchParams()
    if (schoolId) params.append('school_id', schoolId)
    
    return api.get<ApiResponse<any[]>>(`/joining-reports/teachers?${params.toString()}`)
  },

  markAsViewed: (id: number) =>
    api.patch<ApiResponse>(`/joining-reports/${id}/viewed`),

  markAsVerified: (id: number, verificationNotes?: string) =>
    api.patch<ApiResponse>(`/joining-reports/${id}/verified`, {
      verification_notes: verificationNotes
    }),

  downloadFile: (id: number) =>
    api.get<Blob>(`/joining-reports/${id}/download`, {
      responseType: 'blob'
    }),

  delete: (id: number) =>
    api.delete<ApiResponse>(`/joining-reports/${id}`),

  approveAndForward: (id: number, recipients: Array<{role: string, office_id?: string}>) =>
    api.post<ApiResponse>(`/joining-reports/${id}/approve-forward`, {
      recipients
    }),

  // Sidebar badge: teacher = transfer order issued, joining report not yet submitted; admin = submitted reports pending action
  getSidebarBadgeCount: () =>
    api.get<ApiResponse<JoiningReport[]> & { pagination?: { total: number; page: number; limit: number; pages: number } }>(
      '/joining-reports/sidebar-badge-count'
    ),
}

